// Utilitários compartilhados para o novo formato estruturado de refeições
// (nome/horário + opções alternativas + itens com quantidade/unidade/observação
// + substituições), usado no formulário de dieta e na tela da aluna.

let seq = 0;
function uid(prefix) {
  seq += 1;
  return `${prefix}_${Date.now()}_${seq}_${Math.floor(Math.random() * 1e6)}`;
}

export function substituicaoVazia() {
  return { id: uid('sub'), nome: '', quantidade: '', unidade: '' };
}

export function itemVazio() {
  return { id: uid('item'), nome: '', quantidade: '', unidade: '', observacao: '', substituicoes: [] };
}

export function opcaoVazia() {
  return { titulo: '', itens: [itemVazio()], receita: '' };
}

export function refeicaoVazia() {
  return { id: uid('ref'), nome: '', horario: '', observacao: '', opcoes: [opcaoVazia()] };
}

// Converte um item no formato legado (string "nome: quantidade" ou objeto parcial)
// para o formato estruturado atual.
function normalizarItem(raw) {
  if (raw && typeof raw === 'object') {
    return {
      id: raw.id || uid('item'),
      nome: raw.nome || '',
      quantidade: raw.quantidade || '',
      unidade: raw.unidade || '',
      observacao: raw.observacao || '',
      substituicoes: Array.isArray(raw.substituicoes) ? raw.substituicoes.map(s => ({
        id: s.id || uid('sub'), nome: s.nome || '', quantidade: s.quantidade || '', unidade: s.unidade || '',
      })) : [],
    };
  }
  const texto = String(raw || '').trim();
  const sep = texto.indexOf(':');
  const nome = sep > -1 ? texto.slice(0, sep).trim() : texto;
  const resto = sep > -1 ? texto.slice(sep + 1).trim() : '';
  return { id: uid('item'), nome, quantidade: resto, unidade: '', observacao: '', substituicoes: [] };
}

// Converte uma refeição no formato legado ({ itens: string | string[] }) — ou já
// no formato atual ({ opcoes: [...] }) — garantindo sempre o formato estruturado
// com `opcoes[].itens[]`. Idempotente: aplicar de novo numa refeição já normalizada
// não muda nada.
export function normalizarRefeicao(ref) {
  if (!ref) return refeicaoVazia();
  if (Array.isArray(ref.opcoes) && ref.opcoes.length > 0) {
    return {
      id: ref.id || uid('ref'),
      nome: ref.nome || '',
      horario: ref.horario || '',
      observacao: ref.observacao || '',
      opcoes: ref.opcoes.map(op => ({
        titulo: op.titulo || '',
        itens: Array.isArray(op.itens) && op.itens.length > 0 ? op.itens.map(normalizarItem) : [itemVazio()],
        receita: op.receita || '',
      })),
    };
  }
  const brutos = Array.isArray(ref.itens)
    ? ref.itens
    : String(ref.itens || '').split(/[,;\n]/).map(s => s.trim()).filter(Boolean);
  return {
    id: ref.id || uid('ref'),
    nome: ref.nome || '',
    horario: ref.horario || '',
    observacao: ref.observacao || '',
    opcoes: [{ titulo: '', itens: brutos.length > 0 ? brutos.map(normalizarItem) : [itemVazio()], receita: '' }],
  };
}

export function normalizarRefeicoes(refeicoes) {
  if (!refeicoes) return [];
  if (typeof refeicoes === 'string') {
    try {
      const parsed = JSON.parse(refeicoes);
      return normalizarRefeicoes(parsed);
    } catch {
      return String(refeicoes).split(/\n{2,}/).map(bloco => normalizarRefeicao({ nome: '', itens: bloco })).filter(ref => ref.opcoes?.[0]?.itens?.length);
    }
  }
  if (Array.isArray(refeicoes)) return refeicoes.map(normalizarRefeicao);
  if (typeof refeicoes === 'object') return [normalizarRefeicao(refeicoes)];
  return [];
}

// Junta quantidade + unidade num texto único para exibição (ex: "200 grama(s) (200g)").
export function formatQuantidade(item) {
  return [item?.quantidade, item?.unidade].filter(Boolean).join(' ').trim();
}

// Receitas cadastradas nas opções das refeições, agrupadas por refeição.
export function gerarReceitas(refeicoes) {
  const lista = [];
  (refeicoes || []).forEach(ref => {
    (ref.opcoes || []).forEach(op => {
      if (op.receita?.trim()) lista.push({ refeicao: ref.nome, opcao: op.titulo, receita: op.receita.trim() });
    });
  });
  return lista;
}

// Lista de compras: nomes únicos (alimentos + substituições) de todas as refeições,
// ordenados alfabeticamente. Refeições já devem estar normalizadas.
export function gerarListaDeCompras(refeicoes) {
  const nomes = new Set();
  (refeicoes || []).forEach(ref => {
    (ref.opcoes || []).forEach(op => {
      (op.itens || []).forEach(item => {
        if (item.nome) nomes.add(item.nome.trim());
        (item.substituicoes || []).forEach(s => { if (s.nome) nomes.add(s.nome.trim()); });
      });
    });
  });
  return Array.from(nomes).sort((a, b) => a.localeCompare(b, 'pt-BR'));
}
